import { Logger } from '@kinopoisk-int/logger';

import { clientConfig } from '@/config/client';

interface MetrikaParams {
    target: string;
    counterId: string;
    params?: any; // TODO: fix types, OTT-6666
    logger: Logger;
}

type AppMetrikaParams = Omit<MetrikaParams, 'counterId'>;

interface SendReachGoal {
    counter: Counter;
    target: string;
    params: Record<string, unknown>;
    logger: Logger;
}

interface Counter {
    reachGoal(target: string, params: Record<string, unknown>): void;
    params(params: Record<string, unknown>): void;
}

interface GoalsQueue {
    [counterId: string]: Goal[];
}

interface Goal {
    target: MetrikaParams['target'];
    params: MetrikaParams['params'];
}

declare global {
    interface Window {
        [key: string]: Counter | any;
    }
}

const { yandexMetrika } = clientConfig;

const MAIN_COUNTER_ID = `yaCounter${yandexMetrika.main}`;

const goalsQueue: GoalsQueue = {};
const COUNTER_POLL_INTERVAL = 200;
let COUNTER_POLL_INTERVAL_ID: number;

const REACH_GOAL_ON_FAIL_MESSAGE = 'Metrika: reachGoal failed';

const attempt = (fn: () => void, logger: Logger, onFailMessage: string) => {
    try {
        fn();
    } catch (error) {
        if (error instanceof Error) logger.error({ error }, onFailMessage);
        return false;
    }

    return true;
};

const getCounter = (counterId: string) => window[counterId];

const sendReachGoal = ({ counter, target, params, logger }: SendReachGoal) => {
    attempt(() => counter.reachGoal(target, params), logger, REACH_GOAL_ON_FAIL_MESSAGE);
};

const enqueueReachGoal = ({ target, params, counterId = MAIN_COUNTER_ID }: MetrikaParams) => {
    if (!goalsQueue[counterId]) {
        goalsQueue[counterId] = [];
    }

    goalsQueue[counterId]?.push({ target, params });
};

const tryProcessQueue = (logger: Logger) => {
    let shouldClearInterval = true;

    const processCounterQueue = (counterId: string) => {
        const counter = getCounter(counterId);

        if (!counter) {
            shouldClearInterval = false;
            return;
        }

        goalsQueue[counterId]?.forEach(({ target, params }) => {
            sendReachGoal({ counter, target, params, logger });
        });

        delete goalsQueue[counterId];
    };

    Object.keys(goalsQueue).forEach(processCounterQueue);

    if (shouldClearInterval) {
        clearInterval(COUNTER_POLL_INTERVAL_ID);
    }
};

const startPolling = (logger: Logger) => {
    if (COUNTER_POLL_INTERVAL_ID) {
        return;
    }

    COUNTER_POLL_INTERVAL_ID = window.setInterval(
        () => tryProcessQueue(logger),
        COUNTER_POLL_INTERVAL
    );
};

const reachGoal = ({ target, params, counterId, logger }: MetrikaParams) => {
    const counter = getCounter(counterId);

    if (counter) {
        sendReachGoal({ counter, target, params, logger });
    } else {
        enqueueReachGoal({ target, params, counterId, logger });
        startPolling(logger);
    }
};

/**
 * Trigger sendGoals
 * @param {String} target — Target name
 * @param {String} counterId — counterId
 * @param {Object=} params — Additional parameters
 */

const emitMetrika = ({ target, params, counterId, logger }: MetrikaParams) => {
    attempt(
        () => reachGoal({ target, params, counterId, logger }),
        logger,
        REACH_GOAL_ON_FAIL_MESSAGE
    );
};

export const emitServiceMetrika = ({ target, params, logger }: AppMetrikaParams) => {
    emitMetrika({ target, params, counterId: MAIN_COUNTER_ID, logger });
};
